import React, { useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';

import PointsDisplay from 'components/PointsDisplay';

import ReactTooltip from 'react-tooltip';
import { capitalize } from 'lodash';

import { useClassName } from '@travpro/hooks';

import Modal from 'components/Modal';
import ShareBox from 'components/ShareBox';

/**
 * A display component used to show the user's current progress.
 * @param {string[]} tierBadges An array of badge image urls, one for each tier.
 * @param {string[]} tierRewardBadges An array of badge image urls, one for each tier. If available, they are used as an alternative to the tierBadges for the popout image.
 * @param {number} tierIndex The index of the current tier.
 * @param {Object[]} tiers An array of tier objects, each containing a title and a points value.
 * @param {number} totalPoints The total number of points the user has.
 * @param {string} title The title of the badge display.
 * @param {string} layout Layout style defines the look or shape of the badge display. A horizontal bar, a vertical pilar or a compact box.
 * @param {string} position Position defines the location of the badge display, either a regular inline component or a fixed element on the left or right side of the screen.
 * @param {boolean} float Float defines whether the badge display should float/stick on the screen or not. Only applies when position is set to left or right.
 * @returns {React.Component} A component that displays a badge with the user's current status and points (implemnted with PointsDisplay)
 */
const BadgeDisplay = ({
  tierBadges,
  popoutBadges,
  tierIndex,
  tiers,
  totalPoints,
  badgeSubtitleOverride,
  pointsBarSubtitle,
  layout = 'box',
  position = 'inline',
  hasPopout = false,
  hideBadge = false,
}) => {
  // Bail out if we don't have the required props (yet)
  const requiredProps = [tiers, totalPoints];
  if (requiredProps.some((prop) => prop === undefined || prop === null)) {
    return null;
  }

  const [showBadge, setShowBadge] = useState(false);

  const handleShowBadge = () => {
    // checks if there is a popoutBadge for the current tierIndex
    if (popoutBadges[tierIndex] === 'none') return;
    setShowBadge(true);
  };
  const handleCloseBadge = () => {
    setShowBadge(false);
  };

  const c = useClassName('badge-display');
  const layouts = ['box', 'bar', 'pillar', 'bigBar', 'transparantBar'];
  const barShapedLayouts = ['bar', 'bigBar', 'transparantBar'];

  // Wraps badge in a fancybox <a /> if a popout image should have a popout and it is available
  const popoutWrapper = (element) => {
    // if no tierIndex is provided, return no elemenat (hide badge)
    if (tierIndex === null || tierIndex === undefined) return null;

    // !hasPopout OR hasPopout + alternate tierRewardBadges + no specific alternate reward badge = no fancybox
    if (!hasPopout) return element;

    // hasPopout AND popoutBadges, but no alternate badge for that index = no fancybox
    if (popoutBadges[tierIndex] === 'none') return element;

    // hasPopout AND an available popoutBadge for that index = fancybox with alternate image
    return (
      <a {...c('badge-link')} onClick={handleShowBadge}>
        {element}
      </a>
    );
  };

  const popoutImage = popoutBadges && popoutBadges[tierIndex] ? popoutBadges[tierIndex] : tierBadges[tierIndex];

  return (
    <React.Fragment>
      <Modal id="progress-badge-modal" show={showBadge} onHide={handleCloseBadge} bgClose>
        <ShareBox
          image={popoutImage}
          url={popoutImage}
          title={capitalize(tiers[tierIndex]?.title)}
          subject={i18n.translate('sharing.shareSubjectBadge')}
          body={i18n.translate('sharing.shareBodyProgress', {
            rank: tiers[tierIndex]?.title,
          })}
          email
          download
          size="sm"
        />
      </Modal>
      <div
        {...c({
          [`${layout}`]: layouts.includes(layout) && layout !== 'box',
          box: !layouts.includes(layout) || layout === 'box',
          static: ['left', 'right'].includes(position),
          [`${position}`]: ['left', 'right', 'inline'].includes(position),
          inline: !['left', 'right'].includes(position),
        })}
      >
        <div
          {...c('inner', {
            horizontal: !barShapedLayouts.includes(layout),
            vertical: barShapedLayouts.includes(layout),
          })}
        >
          <div {...c('subcontainer')}>
            {!hideBadge && popoutWrapper(<img {...c('badge')} src={Object.values(tierBadges)[tierIndex]} alt="Logo" />)}

            {tierIndex !== null && badgeSubtitleOverride !== '' && (
              <span {...c('wrapper-span', 'badge-subtitle')}>
                {badgeSubtitleOverride
                  ? badgeSubtitleOverride
                  : `${i18n.translate('trainingScreens.status')} ${tiers[tierIndex].title}`}
              </span>
            )}
            <ReactTooltip />
          </div>
          <div
            {...c('divider', {
              horizontal: !['bar', 'bigBar', 'transparantBar'].includes(layout),
              vertical: ['bar', 'bigBar'].includes(layout),
            })}
          />
          <div {...c('subcontainer')}>
            <span {...c('wrapper-span', 'pointsbar')}>
              <PointsDisplay
                orientation={['pillar'].includes(layout) ? 'vertical' : 'horizontal'}
                tiers={tiers.map(({ points: p }) => p)}
                labels={tiers.map(({ title }) => capitalize(title))}
                // minSize={'40rem'}
                points={totalPoints}
                segmented
              />
            </span>
            <span {...c('wrapper-span', 'pointsbar-subtitle')}>{pointsBarSubtitle}</span>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BadgeDisplay;

BadgeDisplay.propTypes = {
  theme: PropTypes.oneOf(['box', 'bar', 'pillar', 'bigBar']),
  position: PropTypes.oneOf(['right', 'left', 'inline']),
  float: PropTypes.bool,
  hideBadge: PropTypes.bool,
};

BadgeDisplay.defaultProps = {
  theme: 'box',
  position: 'inline',
  float: false,
};
